/**
 * CustomerPriceListDialog
 *
 * Modal for downloading a customer-specific price list PDF.
 * Auto-selects the customer's pricing tier from their branch type,
 * lets the user pick which grades to include, then generates and downloads the PDF.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import { toLocalDateString } from '@/utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Download, Loader2 } from 'lucide-react'
import {
  fetchCustomerWithPricingContext,
  fetchProductPrices,
  fetchProductTypes,
  fetchPriceTiers,
  fetchVolumeDiscounts,
  fetchPromotions,
} from '@/supabase-client'
import { buildVolumeDiscountRow } from '@/utils/priceListUtils'
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown'
import {
  filterCurrentAndUpcoming,
  filterByTierAndCustomerType,
  promoOptionLabel,
  promoToPdfRow,
} from '@/utils/promotionUtils'

/**
 * Transform product prices into grouped format matching PriceListPdf expectations.
 */
function buildGroupedPrices(prices) {
  const grouped = {}

  // Only include active products and active grades
  prices.filter(p => p.product?.active && p.product_type?.active).forEach(price => {
    const productId = price.product_id
    const productName = price.product?.strain || 'Unknown'

    if (!grouped[productId]) {
      grouped[productId] = {
        productId,
        productName,
        basePrice: price.product?.base_price_per_kg || 0,
        limitedAvailability: price.product?.limited_availability || false,
        grades: {}
      }
    }

    const gradeId = price.product_type_id
    const gradeName = price.product_type?.name || 'Unknown'

    if (!grouped[productId].grades[gradeId]) {
      grouped[productId].grades[gradeId] = {
        gradeId,
        gradeName,
        multiplier: price.product_type?.price_multiplier || 1.0,
        tiers: {}
      }
    }

    const tierName = price.price_tier?.name || 'unknown'
    grouped[productId].grades[gradeId].tiers[tierName] = {
      id: price.id,
      price: parseFloat(price.price_per_kg),
      tierDisplayName: price.price_tier?.display_name || tierName
    }
  })

  return Object.values(grouped)
    .sort((a, b) => a.productName.localeCompare(b.productName))
}

/**
 * Transform grouped prices into PriceGroup[] for PriceListPdf.
 */
function toPriceGroups(groupedPrices, { selectedTiers, selectedGrades }) {
  return groupedPrices
    .map(product => ({
      product_name: product.productName,
      base_price: product.basePrice,
      limited_availability: product.limitedAvailability || false,
      grades: Object.values(product.grades)
        .filter(grade => selectedGrades.size === 0 || selectedGrades.has(grade.gradeId))
        .sort((a, b) => b.multiplier - a.multiplier)
        .map(grade => ({
          grade_name: grade.gradeName,
          multiplier: grade.multiplier,
          tiers: Object.fromEntries(
            Object.entries(grade.tiers)
              .filter(([tierName]) => selectedTiers.has(tierName))
              .map(([tierName, tierData]) => [tierName, tierData.price])
          ),
        }))
        .filter(grade => Object.values(grade.tiers).some(p => p > 0)),
    }))
    .filter(group => group.grades.length > 0)
}

export function CustomerPriceListDialog({ open, onOpenChange, customerId, customerName }) {
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  // Data
  const [customerTier, setCustomerTier] = useState(null)
  const [customerTypeId, setCustomerTypeId] = useState(null)
  const [groupedPrices, setGroupedPrices] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [allPromotions, setAllPromotions] = useState([])

  // Selection
  const [selectedGrades, setSelectedGrades] = useState(() => new Set())
  const [selectedPromotions, setSelectedPromotions] = useState(() => new Set())
  const [includeVolumePricing, setIncludeVolumePricing] = useState(true)

  const availablePromotions = useMemo(() => {
    if (!customerTier) return []
    return filterByTierAndCustomerType(allPromotions, {
      tierNames: new Set([customerTier.name]),
      customerTypeId,
    })
  }, [allPromotions, customerTier, customerTypeId])

  useEffect(() => {
    if (!open || !customerId) return

    const loadData = async () => {
      setLoading(true)
      setError(null)
      try {
        const [customer, prices, types, tiers, promotionsRaw] = await Promise.all([
          fetchCustomerWithPricingContext(customerId),
          fetchProductPrices(),
          fetchProductTypes(),
          fetchPriceTiers(),
          fetchPromotions(),
        ])

        // Determine the customer's pricing tier from their branch customer type
        const branches = customer.branches || []

        // Find the tier object — use the first branch's tier (most common case)
        const primaryTierId = branches[0]?.customer_types?.default_price_tier_id
        const primaryCustomerTypeId = branches[0]?.customer_types?.id || null
        const tier = tiers.find(t => t.id === primaryTierId)

        if (!tier) {
          setError('No pricing tier found for this customer. Check their customer type has a default price tier assigned.')
          setLoading(false)
          return
        }

        setCustomerTier(tier)
        setCustomerTypeId(primaryCustomerTypeId)
        setGroupedPrices(buildGroupedPrices(prices))
        setProductTypes(types.filter(t => t.active))
        setAllPromotions(filterCurrentAndUpcoming(promotionsRaw || []))
        setSelectedGrades(new Set()) // Reset on open
        setSelectedPromotions(new Set())
      } catch (err) {
        console.error('Failed to load pricing data:', err)
        setError('Failed to load pricing data.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [open, customerId])

  const handleDownload = useCallback(async () => {
    if (!customerTier) return
    setGenerating(true)
    try {
      const [{ pdf }, { PriceListPdf }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../PriceListPdf'),
      ])

      const today = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })

      const selectedTiers = new Set([customerTier.name])
      const pdfTiers = [{ key: customerTier.name, displayName: customerTier.display_name }]
      const pdfGroups = toPriceGroups(groupedPrices, { selectedTiers, selectedGrades })

      const discountsRaw = includeVolumePricing ? await fetchVolumeDiscounts() : []
      const volumeDiscounts = discountsRaw
        .filter(d => d.active !== false)
        .map(d => buildVolumeDiscountRow(d, pdfGroups))

      const promotionsForPdf = availablePromotions
        .filter(p => selectedPromotions.has(p.id))
        .map(promoToPdfRow)

      const priceListBlob = await pdf(
        PriceListPdf({
          grouped: pdfGroups,
          tiers: pdfTiers,
          generatedDate: today,
          volumeDiscounts,
          promotions: promotionsForPdf,
          hideGrade: false,
        })
      ).toBlob()

      // Merge with cover page
      const { PDFDocument } = await import('pdf-lib')
      const [coverBytes, priceListBytes] = await Promise.all([
        fetch('/images/price-list-cover.pdf').then(r => r.arrayBuffer()),
        priceListBlob.arrayBuffer(),
      ])

      const merged = await PDFDocument.create()
      const coverDoc = await PDFDocument.load(coverBytes)
      const priceListDoc = await PDFDocument.load(priceListBytes)

      const [coverPage] = await merged.copyPages(coverDoc, [0])
      merged.addPage(coverPage)

      const priceListPages = await merged.copyPages(priceListDoc, priceListDoc.getPageIndices())
      priceListPages.forEach(page => merged.addPage(page))

      const mergedBytes = await merged.save()
      const blob = new Blob([mergedBytes], { type: 'application/pdf' })

      const safeName = customerName.replace(/[^a-zA-Z0-9]/g, '-')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `GMC-Price-List-${safeName}-${toLocalDateString(new Date())}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      onOpenChange(false)
    } catch (err) {
      console.error('PDF generation failed:', err)
      if (err.message?.includes('dynamically imported module') || err.message?.includes('Failed to fetch')) {
        toast.error('App was updated — please refresh the page and try again.')
      } else {
        toast.error('Failed to generate PDF. Please try again.')
      }
    } finally {
      setGenerating(false)
    }
  }, [customerTier, groupedPrices, selectedGrades, includeVolumePricing, availablePromotions, selectedPromotions, customerName, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download Price List</DialogTitle>
          <DialogDescription>
            {customerTier
              ? `${customerName} — ${customerTier.display_name} pricing`
              : `Generate a price list for ${customerName}`
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive py-4">{error}</p>
        ) : (
          <div className="space-y-4 py-2">
            {/* Grade selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Grades</label>
              <MultiSelectDropdown
                options={productTypes.map(t => ({ id: t.id, label: t.name }))}
                selected={selectedGrades}
                onChange={setSelectedGrades}
                allLabel="All Grades"
                width="w-full"
              />
            </div>

            {/* Promotions */}
            <div>
              <label className="text-sm font-medium mb-2 block">Promotions</label>
              <MultiSelectDropdown
                options={availablePromotions.map(p => ({ id: p.id, label: promoOptionLabel(p) }))}
                selected={selectedPromotions}
                onChange={setSelectedPromotions}
                allLabel="None"
                width="w-full"
                emptyLabel="No active or upcoming promotions for this customer"
              />
            </div>

            {/* Options */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={includeVolumePricing}
                onChange={(e) => setIncludeVolumePricing(e.target.checked)}
                className="rounded border-slate-300"
              />
              Include volume pricing
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={generating || loading || !!error}
            className="gap-2"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
